import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Edit2,
  Trash2,
  Loader2,
} from 'lucide-react';
import {
  deleteModule,
  createElement,
  deleteElement,
} from '@/services/filieresService';
import type { ModuleWithElements, WriteGuard } from '@/types/filiere.types';
import EditModuleModal from './EditModuleModal';
import DeleteImpactModal from './filieres/DeleteImpactModal';

interface ModuleCardProps {
  module: ModuleWithElements;
  onUpdate: () => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
  canManage?: boolean;
  /** Wraps every write so the page can show a campus-scope warning first */
  guardWrite?: WriteGuard;
}

const runDirectly: WriteGuard = (action) => action();

const ModuleCard: React.FC<ModuleCardProps> = ({
  module,
  onUpdate,
  onError,
  onSuccess,
  canManage = true,
  guardWrite = runDirectly,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Element creation
  const [newElementName, setNewElementName] = useState('');
  const [isAddingElement, setIsAddingElement] = useState(false);


  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);

  // Delete confirmation modals
  const [showDeleteModuleModal, setShowDeleteModuleModal] = useState(false);
  const [showDeleteElementModal, setShowDeleteElementModal] = useState(false);
  const [elementToDelete, setElementToDelete] = useState<{ id: string; name: string } | null>(null);

  const [isDeletingElement, setIsDeletingElement] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteModule(module.id);
      onSuccess('Module supprimé');
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
      setShowDeleteModuleModal(false);
    }
  };

  const handleAddElement = async () => {
    if (!newElementName.trim()) return;

    setIsAddingElement(true);
    try {
      await createElement({ moduleId: module.id, name: newElementName.trim() });
      onSuccess('Élément ajouté');
      setNewElementName('');
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de l\'ajout');
    } finally {
      setIsAddingElement(false);
    }
  };

  const handleDeleteElement = async () => {
    if (!elementToDelete) return;
    setIsDeletingElement(true);

    try {
      await deleteElement(elementToDelete.id);
      onSuccess('Élément supprimé');
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setIsDeletingElement(false);
      setShowDeleteElementModal(false);
      setElementToDelete(null);
    }
  };

  const openDeleteElementModal = (elementId: string, elementName: string) => {
    setElementToDelete({ id: elementId, name: elementName });
    setShowDeleteElementModal(true);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
        <div
          className="flex items-center gap-3 flex-1 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="w-8 h-8 rounded bg-primary-100 flex items-center justify-center">
            <span className="text-xs font-bold text-primary-700">{module.code}</span>
          </div>

          <div className="flex-1">
            <h4 className="font-medium text-gray-900">{module.name}</h4>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span>Coefficient: {module.coefficient}</span>
              <span>•</span>
              <span>{module.elements.length} élément(s)</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {canManage && (
            <>
              <button
                onClick={() => guardWrite(() => setShowEditModal(true))}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                title="Modifier"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => guardWrite(() => setShowDeleteModuleModal(true))}
                disabled={isDeleting}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                title="Supprimer"
              >
                {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </>
          )}

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Content - Elements */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-100">
          {/* Elements List */}
          {module.elements.length > 0 && (
            <div className="space-y-2 mb-4">
              {module.elements.map((element) => (
                <div
                  key={element.id}
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-primary-400" />
                    <span className="text-sm text-gray-700">{element.name}</span>
                    <span className="text-xs text-gray-400">({element.weight.toFixed(1)}%)</span>
                    {element.volumeHoraire && (
                      <span className="text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                        {element.volumeHoraire}h
                      </span>
                    )}
                  </div>
                  {canManage && (
                    <button
                      onClick={() => guardWrite(() => openDeleteElementModal(element.id, element.name))}
                      title="Supprimer l'élément"
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add Element */}
          {canManage && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newElementName}
              onChange={(e) => setNewElementName(e.target.value)}
              placeholder="Nom de l'élément de module..."
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') guardWrite(handleAddElement);
              }}
            />
            <button
              onClick={() => guardWrite(handleAddElement)}
              disabled={isAddingElement || !newElementName.trim()}
              className="flex items-center gap-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAddingElement ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Ajouter
            </button>
          </div>
          )}
        </div>
      )}

      {/* Edit Module Modal */}
      {showEditModal && (
        <EditModuleModal
          module={module}
          onClose={() => setShowEditModal(false)}
          onSuccess={onSuccess}
          onError={onError}
          onUpdate={onUpdate}
        />
      )}

      {/* Delete Module Confirmation (with impact) */}
      <DeleteImpactModal
        isOpen={showDeleteModuleModal}
        targets={[{ type: 'module', id: module.id }]}
        title="Supprimer ce module ?"
        itemName={module.name}
        message="Le module et tous ses éléments seront supprimés, ainsi que les données rattachées ci-dessous."
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModuleModal(false)}
      />

      {/* Delete Element Confirmation (with impact) */}
      <DeleteImpactModal
        isOpen={showDeleteElementModal && elementToDelete !== null}
        targets={elementToDelete ? [{ type: 'element', id: elementToDelete.id }] : []}
        title="Supprimer cet élément ?"
        itemName={elementToDelete?.name}
        message="L'élément de module sera supprimé, ainsi que les données rattachées ci-dessous."
        isLoading={isDeletingElement}
        onConfirm={handleDeleteElement}
        onCancel={() => {
          setShowDeleteElementModal(false);
          setElementToDelete(null);
        }}
      />
    </div>
  );
};

export default ModuleCard;
